/**
 * Per-tab workspace context.
 *
 * Tabs share cookies + localStorage but not sessionStorage — so the
 * workspace pin lives here. Every API request attaches X-Workspace-Id
 * from this store; the backend prefers that header over the shared
 * cookie. The cookie stays as the "default for new tabs / initial load."
 *
 * Two slots: ID (workspace UUID or the literal 'home' sentinel) and
 * companyName (used for workspace-scoped Zustand persist keys). The ID
 * slot is pinned and only mutated by explicit switch/clear/logout;
 * companyName is refreshed from every /me response so a stale value
 * (e.g. from a poisoned setHome('')) self-heals on the next profile
 * fetch.
 */

import LocalStorageKey from '@enums/LocalStorageKey';
import LocalStorageService from '@services/LocalStorageService';

const ID_KEY = 'active_workspace_id';
const COMPANY_KEY = 'active_workspace_company_name';
const USER_KEY = 'active_workspace_user_id';
const HOME_SENTINEL = 'home';
let scopeGeneration = 0;

/** Read the current user's id from persisted profile, or null if not logged in yet. */
function currentUserId(): string | null {
  const profile = LocalStorageService.getItem<{ userId?: string }>(
    LocalStorageKey.USER_PROFILE,
  );
  return profile?.userId ?? null;
}

function safeGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage disabled (Safari private mode, etc.) — silently no-op.
  }
}

function safeRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // no-op
  }
}

const WorkspaceContextService = {
  /** True when this tab has an explicit workspace pin set. */
  hasPin(): boolean {
    const id = safeGet(ID_KEY);
    return id !== null && id !== '';
  },

  /**
   * Value to attach as the X-Workspace-Id header, or null when the tab
   * has no override and the backend should fall back to the cookie.
   */
  headerValue(): string | null {
    const raw = safeGet(ID_KEY);
    if (raw === null || raw === '') return null;
    return raw;
  },

  /** Pinned companyName, used to scope Zustand persist keys. */
  companyName(): string | null {
    const v = safeGet(COMPANY_KEY);
    return v === '' ? null : v;
  },

  /** userId this tab's pin belongs to — for detecting user swaps. */
  userId(): string | null {
    return safeGet(USER_KEY);
  },

  /** Fence async work across switches, including leaving and returning to the same tenant. */
  captureScopeGuard(): () => boolean {
    const generation = scopeGeneration;
    const workspace = safeGet(ID_KEY);
    const company = safeGet(COMPANY_KEY);
    const user = currentUserId();
    return () => generation === scopeGeneration
      && workspace === safeGet(ID_KEY)
      && company === safeGet(COMPANY_KEY)
      && user === currentUserId();
  },

  /** Pin this tab to a workspace by id + companyName. */
  setWorkspace(workspaceId: string, companyName: string): void {
    if (!workspaceId) return;
    if (workspaceId !== safeGet(ID_KEY) || (companyName && companyName !== safeGet(COMPANY_KEY))) scopeGeneration += 1;
    safeSet(ID_KEY, workspaceId);
    if (companyName) safeSet(COMPANY_KEY, companyName);
    const uid = currentUserId();
    if (uid) safeSet(USER_KEY, uid);
  },

  /** Pin this tab to the home company, overriding the shared cookie. */
  setHome(companyName: string): void {
    if (safeGet(ID_KEY) !== HOME_SENTINEL || (companyName && companyName !== safeGet(COMPANY_KEY))) scopeGeneration += 1;
    safeSet(ID_KEY, HOME_SENTINEL);
    if (companyName) safeSet(COMPANY_KEY, companyName);
    const uid = currentUserId();
    if (uid) safeSet(USER_KEY, uid);
  },

  /**
   * Seed the pin if this tab has no pin yet. Called after the initial
   * /me so we capture whatever workspace the shared cookie pointed at
   * BEFORE another tab has a chance to change it.
   */
  seedIfUnpinned(params: {
    workspaceId: string | null;
    companyName: string;
  }): void {
    if (WorkspaceContextService.hasPin()) return;
    if (params.workspaceId) {
      WorkspaceContextService.setWorkspace(params.workspaceId, params.companyName);
    } else {
      WorkspaceContextService.setHome(params.companyName);
    }
  },

  /** True when this pin was written by a different user (indicates user swap). */
  pinBelongsToDifferentUser(currentUserIdArg: string): boolean {
    const pinnedUser = safeGet(USER_KEY);
    return pinnedUser !== null && pinnedUser !== '' && pinnedUser !== currentUserIdArg;
  },

  /**
   * Always sync companyName from a fresh profile response. The ID pin
   * stays; only companyName is refreshed. This heals a stale/empty
   * COMPANY_KEY (e.g. from a caller that once wrote setHome('')) once
   * we know the correct value.
   */
  syncCompanyName(companyName: string): void {
    if (companyName && companyName !== safeGet(COMPANY_KEY)) scopeGeneration += 1;
    if (companyName) safeSet(COMPANY_KEY, companyName);
  },

  /** Wipe on logout / user swap so the next session starts clean. */
  clear(): void {
    scopeGeneration += 1;
    safeRemove(ID_KEY);
    safeRemove(COMPANY_KEY);
    safeRemove(USER_KEY);
  },
} as const;

export default WorkspaceContextService;
